import { ArrowRight, ChevronDown } from "lucide-react";
import { useState } from "react";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

const FAQ = () => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const faqData: FAQItem[] = [
    {
      id: "1",
      question: "How does Fleet Pro track vehicles?",
      answer:
        "Fleet Pro uses advanced GPS technology combined with telematics devices installed in your vehicles. These devices provide real-time location tracking, route monitoring, and comprehensive vehicle diagnostics. Our system updates location data every 30 seconds and provides detailed reports on driver behavior, fuel consumption, and maintenance needs.",
    },
    {
      id: "2",
      question: "How does Fleet Pro track vehicles?",
      answer:
        "Fleet Pro tracks vehicles through a combination of GPS satellites and cellular networks. The tracking devices are professionally installed and provide 24/7 monitoring capabilities. You can access all tracking data through our web dashboard or mobile app, with features like geofencing, speed alerts, and historical route playback.",
    },
    {
      id: "3",
      question: "Can I upgrade from the Starter Plan to the Professional Plan?",
      answer:
        "Yes, you can upgrade from the Starter Plan to the Professional Plan at any time. The upgrade is seamless and takes effect immediately. You'll gain access to advanced features like custom reporting, API integration, unlimited users, and priority customer support. Any remaining time on your current plan will be prorated toward your new subscription.",
    },
    {
      id: "4",
      question: "Can I upgrade from the Starter Plan to the Professional Plan?",
      answer:
        "Absolutely! Upgrading is simple and can be done directly from your account dashboard. When you upgrade, you'll immediately unlock all Professional Plan features including advanced analytics, white-label options, and dedicated account management. Your billing cycle will adjust automatically, and you'll only pay the difference for the remaining period.",
    },
    {
      id: "5",
      question: "Can I upgrade from the Starter Plan to the Professional Plan?",
      answer:
        "Yes, plan upgrades are available 24/7 through your account settings. The Professional Plan includes everything from the Starter Plan plus enhanced security features, custom integrations, and dedicated technical support. The transition is instant, and all your existing data and configurations are preserved during the upgrade process.",
    },
  ];

  const toggleItem = (id: string) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id);
    } else {
      newOpenItems.add(id);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <div className="w-full min-h-screen bg-[#FAFAFA] py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-8 lg:gap-12 xl:gap-16">
          {/* Header Section */}
          <div className="xl:w-2/5 xl:sticky xl:top-8">
            <div className="space-y-6 sm:space-y-8 lg:space-y-12">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-6xl font-bold text-gray-900 leading-tight">
                Got Questions?
                <br />
                We've Got Answers!
              </h1>

              <button className="inline-flex items-center gap-2 text-sm sm:text-base font-medium text-gray-600 hover:text-gray-900 transition-colors duration-200 group">
                View all
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </div>
          </div>

          {/* FAQ Items Section */}
          <div className="xl:w-3/5 xl:max-w-4xl space-y-4 sm:space-y-5">
            {faqData.map((item) => {
              const isOpen = openItems.has(item.id);

              return (
                <div
                  key={item.id}
                  className="bg-[#E7F2F5] rounded-lg sm:rounded-xl lg:rounded-2xl shadow-sm border border-[#E7F2F5] overflow-hidden transition-all duration-200 hover:shadow-md"
                >
                  <button
                    onClick={() => toggleItem(item.id)}
                    className="w-full px-4 sm:px-5 lg:px-6 py-4 sm:py-5 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors duration-200 hover:bg-gray-50"
                    aria-expanded={isOpen}
                    aria-controls={`answer-${item.id}`}
                  >
                    <span className="text-sm sm:text-base lg:text-lg font-medium text-gray-800 pr-3 sm:pr-4 leading-snug">
                      {item.question}
                    </span>

                    <ChevronDown
                      className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform duration-300 ease-out flex-shrink-0 ${
                        isOpen ? "rotate-180 text-gray-600" : ""
                      }`}
                    />
                  </button>

                  <div
                    id={`answer-${item.id}`}
                    className={`overflow-hidden transition-all duration-300 ease-out ${
                      isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="px-4 sm:px-5 lg:px-6 pb-4 sm:pb-5 pt-0">
                      <div className="border-t border-gray-100 pt-3 sm:pt-4">
                        <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
